const USER_KEY = 'user';

/**
 * In-memory CSRF token cache for cross-subdomain scenarios where cookies
 * cannot be read (e.g. different subdomains). Populated from API responses.
 */
let csrfTokenCache: string | null = null;

/**
 * Storage utility for client-side data
 *
 * IMPORTANT: Tokens are stored in httpOnly cookies (managed by backend).
 * This utility only manages user data and CSRF tokens (readable cookies).
 */
export const storage = {
  /**
   * Cache a CSRF token in memory (used when cookie is not readable cross-subdomain)
   */
  setCsrfToken(token: string): void {
    csrfTokenCache = token;
  },

  /**
   * Get CSRF token for including in mutation headers.
   * Returns the in-memory cached token first (populated from API responses),
   * then falls back to reading the cookie directly.
   */
  getCsrfToken(): string | null {
    if (csrfTokenCache !== null) {
      return csrfTokenCache;
    }

    const name = 'csrf_token=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookies = decodedCookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(name)) {
        return cookie.substring(name.length);
      }
    }
    return null;
  },

  /**
   * Store user data in localStorage
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUser(user: any): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  /**
   * Retrieve user data from localStorage
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getUser(): any | null {
    try {
      const user = localStorage.getItem(USER_KEY);
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  },

  /**
   * Remove user data from localStorage
   */
  removeUser(): void {
    localStorage.removeItem(USER_KEY);
  },

  /**
   * Clear all auth-related data
   * Note: httpOnly cookies are cleared by backend on logout
   */
  clearAuth(): void {
    this.removeUser();
    // Also clear in-memory CSRF token cache so stale tokens aren't reused
    csrfTokenCache = null;
  },

  // Legacy methods removed - tokens now in httpOnly cookies
  // setToken() - REMOVED: use httpOnly cookies
  // getToken() - REMOVED: use httpOnly cookies
  // removeToken() - REMOVED: use httpOnly cookies
};
